import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../../config/branding';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableRevenuePointReport,
  RequestReportInterFace,
} from '../../../../interface/reportrevenuePoint.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchRevenueReportPoint,
  updateReportFilters,
  fetchListFilters,
  setReportFiltersData,
  setRevenuePointReportUpdate,
  setSortData,
} from '../../../../redux/slices/ReportRevenuePointSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomDateRangePicker, {
  calculateDateRange,
  normalizePresetKey,
} from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import LocationSidePanel from '../../../../shared-components/table-side-panel/LocationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
  rightTopContent?: React.ReactElement;
}
/**
 * Revenue Report Table for Point
 * @param {ReportTableProperties} properties - The properties
 * @returns {React.ReactElement} The rendered component
 */
function RevenueReportTablePoint(properties: ReportTableProperties) {
  const branding = useAppBranding();
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter, rightTopContent } = properties;

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const previousAccountId = useRef<string | undefined>(undefined);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(() => {
    const defaultRange = calculateDateRange(normalizePresetKey('last14Days'), 'future');
    return {
      startDate: defaultRange?.startDate || '',
      endDate: defaultRange?.endDate || '',
    };
  });
  const previousDateRange = useRef<{ startDate: string; endDate: string }>({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  });

  /**
   * Clears the date range values.
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Render the date filters.
   * @returns {React.JSX.Element} The rendered date filter elements.
   */
  const renderDateFilters = () => {
    return (
      <div className="date-content">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(start, end) => setDateRange({ startDate: start, endDate: end })}
          onClear={clearDateRange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
          disableDates="future"
        />
      </div>
    );
  };

  const {
    sortBy,
    sortField,
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isRevenuePointReportUpdate,
  } = useAppSelector((state) => state.reportRevenuePoint);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  /**
   * Fetches revenue report data for the selected facility.
   * @param {RequestReportInterFace} data - The request parameters for fetching the report.
   */
  const fetchData = useCallback(
    (data: RequestReportInterFace) => {
      if (data.startDate && data.endDate && getDaysBetween(data.startDate, data.endDate) > 31) {
        dispatch(
          setNotificationMessage({
            message: t('dateRangeLimitError'),
            status: true,
            type: 'error',
            code: 400,
          }),
        );
        return;
      }
      if (!loading) {
        dispatch(
          fetchRevenueReportPoint({
            ...data,
            accountCustomId: data.accountCustomId || reportsData?.accountId,
          }),
        );
      }
    },
    [dispatch, loading, reportsData?.accountId, t],
  );
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      sortField,
      sortBy,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  };
  const {
    isEditMode,
    handleEditModeChange,
    handleSidePanelClose,
    renderSidePanelTopLeftContent,
    handleOpenStationSidePanel,
  } = useTableSidePanel({
    onRefresh: handleRefresh,
    sessions: reports,
    disableEdit: true,
    disableDelete: true,
  });

  const column = [
    {
      label: t('sNo'),
      accessorKey: 's_no' as keyof AvailableRevenuePointReport,
      /**
       * S.No view
       * @param {AvailableRevenuePointReport} _report - The report
       * @param {number} index - The index
       * @returns {React.ReactElement} The rendered element
       */
      customData: (_report: AvailableRevenuePointReport, index: number) => (
        <div className="table-row-each-data align-center">{(page - 1) * limit + index + 1}</div>
      ),
      columnWidth: 50,
    },
    {
      label: t('chargingPoint'),
      accessorKey: 'chargingPointSn' as keyof AvailableRevenuePointReport,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableRevenuePointReport,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: (
        <LocationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      sortAllow: false,
      columnWidth: 204,
      /**
       * Station info view
       * @param {AvailableRevenuePointReport} report - The report data
       * @returns {React.ReactElement} The rendered element
       */
      customData: (report: AvailableRevenuePointReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span
              role="presentation"
              className="hyperlink"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenStationSidePanel(report);
              }}>
              {report.chargingStationName}
            </span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="presentation">{report.chargingStationLocation}</span>
          </div>
        </div>
      ),
    },

    {
      label: t('totalRevenue'),
      accessorKey: 'totalRevenue' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      /**
       * Decimal value round of
       * @param {AvailableRevenuePointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableRevenuePointReport) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(row?.totalRevenue)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('sessionCount'),
      accessorKey: 'sessionCount' as keyof AvailableRevenuePointReport,
      sortAllow: true,
    },
    {
      label: t('duration'),
      accessorKey: 'durationSeconds' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      /**
       * Formatted duration
       * @param {AvailableRevenuePointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableRevenuePointReport) => (
        <span>
          {FormatDuration(row?.durationSeconds || (row as any)?.duration, { formatBy: 'sec' })}
        </span>
      ),
    },
    {
      label: t('createdAt', { defaultValue: 'Created At' }),
      accessorKey: 'createdAt' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      columnWidth: 150,
      /**
       * Created at date formatter
       * @param {AvailableRevenuePointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableRevenuePointReport) => (
        <span>
          {ConvertUTCtoDateTime(
            row?.createdAt || (row as any)?.created || (row as any)?.created_at || '',
          ) || '-'}
        </span>
      ),
    },
    {
      label: t('lastUpdated', { defaultValue: 'Last Updated' }),
      accessorKey: 'updatedAt' as keyof AvailableRevenuePointReport,
      sortAllow: true,
      columnWidth: 150,
      /**
       * Updated at date formatter
       * @param {AvailableRevenuePointReport} row - The row
       * @returns {React.ReactElement} The result
       */
      customData: (row: AvailableRevenuePointReport) => (
        <span>
          {ConvertUTCtoDateTime(
            (row as any)?.updatedAt || (row as any)?.lastUpdated || (row as any)?.updated_at || '',
          ) || '-'}
        </span>
      ),
    },
  ];

  /**
   * Current columns for Revenue Point Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('chargingPoint'), accessorKey: 'chargingPointSn' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('totalRevenue'), accessorKey: 'totalRevenue' },
    { label: t('sessionCount'), accessorKey: 'sessionCount' },
    { label: t('duration'), accessorKey: 'durationSeconds' },
    { label: t('createdAt', { defaultValue: 'Created At' }), accessorKey: 'createdAt' },
    { label: t('lastUpdated', { defaultValue: 'Last Updated' }), accessorKey: 'updatedAt' },
  ];

  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });

  /**
   * Export data function
   * @param {string} type - The type of export ('csv' or 'pdf')
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchRevenuePointListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailableRevenuePointReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'chargingPointSn':
                return row?.chargingPointSn || '-';

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'totalRevenue':
                return row?.totalRevenue
                  ? `${ToFixedValue(row.totalRevenue)} ${branding.text.currency}`
                  : `0.00 ${branding.text.currency}`;

              case 'sessionCount':
                return row?.sessionCount ?? (row as any)?.totalSessions ?? '0';

              case 'durationSeconds':
                return row?.durationSeconds || (row as any)?.duration
                  ? FormatDuration(Number(row?.durationSeconds || (row as any)?.duration))
                  : '-';

              case 'createdAt':
                return (row as any)?.createdAt ? ConvertUTCtoDateTime((row as any).createdAt) : '-';

              case 'updatedAt':
                return (row as any)?.updatedAt ? ConvertUTCtoDateTime((row as any).updatedAt) : '-';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      year: reportsData.reportFilter.year,
      page,
      limit,
      accountCustomId: reportsData?.accountId,
    });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {RequestReportInterFace} value - The request parameters for fetching the report
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    if (reportsData?.accountId) {
      fetchData({
        ...GetParametersFromCommonFilters(filters),
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        ...value,
        sortField,
        sortBy,
      });
    }
  };
  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * Fetches revenue report data with query parameters.
   * @param {string} [customAccountId] - The account custom ID.
   * @param {boolean} [resetFilters] - Whether to reset filters.
   */
  const fetchReportData = useCallback(
    (customAccountId?: string, resetFilters = false) => {
      const accountCustomId = customAccountId || reportsData?.accountId;
      const queryParameters = resetFilters ? {} : GetParametersFromCommonFilters(filters);
      if (accountCustomId) {
        fetchData({
          ...queryParameters,
          accountCustomId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          page: DefaultApiLimit.page,
          limit: DefaultApiLimit.limit,
        });
      }
    },
    [filters, dateRange.startDate, dateRange.endDate, reportsData?.accountId, fetchData],
  );

  useEffect(() => {
    const isDateRangeChanged =
      previousDateRange.current.startDate !== dateRange.startDate ||
      previousDateRange.current.endDate !== dateRange.endDate;

    if (
      isFilterUpdated ||
      isRevenuePointReportUpdate ||
      reportsData.isReportUpdated ||
      isDateRangeChanged
    ) {
      if (isDateRangeChanged) {
        previousDateRange.current = dateRange;
      }
      fetchReportData();
      if (reportsData.isReportUpdated) {
        dispatch(resetReportUpdateFlag());
      }
      if (isRevenuePointReportUpdate) {
        dispatch(setRevenuePointReportUpdate(false));
      }
    }
  }, [
    fetchReportData,
    isFilterUpdated,
    isRevenuePointReportUpdate,
    reportsData.isReportUpdated,
    dateRange.startDate,
    dateRange.endDate,
    dispatch,
  ]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'revenueReport' &&
      filters.length > 1 &&
      reportsData.reportByType !== 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (reportsData?.accountId) {
      if (previousAccountId.current !== reportsData.accountId) {
        previousAccountId.current = reportsData.accountId;
        dispatch(fetchListFilters({ accountCustomId: reportsData.accountId }));
        fetchReportData(reportsData.accountId, true);
      }
    }
  }, [reportsData?.accountId, fetchReportData, dispatch]);

  return (
    <div>
      <CustomTable
        disablePagination={false}
        loading={loading}
        limit={limit}
        header={column}
        data={reports}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handleApiRequest}
        version="v2"
        exportDataPdfOrCSV={
          permissionCheck('mobilityRevenueReportExport') ? exportDataPdfOrCSV : undefined
        }
        exportData={reports}
        exportLoader={exportLoader}
        disableHeader={false}
        height={370}
        filters={filters}
        filtersToDispatch={updateReportFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListFilters}
        leftTopContent={leftTopContent}
        refershCurrentPages={ReferenceCurrentPages}
        sizeChangeButtonRequired
        onHiddenColumnsChange={setHiddenColumns}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        rightTopContent={
          <div className="table-bottom-button-list">
            {renderDateFilters()}
            <div className="right-action">{rightTopContent}</div>
          </div>
        }
        onClearFilterChange={() => {
          clearDateRange();
        }}
      />
    </div>
  );
}

export default RevenueReportTablePoint;
